// ===================================================================
// revenueCatService.ts
// Facturation Android native (Google Play Billing via RevenueCat).
// Ne remplace paymentService.ts (Stripe) que sur Android natif — le web
// continue d'utiliser Stripe, qui reste hors du champ du règlement Google Play.
// ===================================================================

import { Capacitor } from '@capacitor/core';
import {
  Purchases,
  type CustomerInfo,
  type PurchasesOffering,
  type PurchasesPackage,
} from '@revenuecat/purchases-capacitor';
import subscriptionService, { SubscriptionTier } from './subscriptionService';

// Entitlement unique configuré dans le dashboard RevenueCat (voir plan de
// migration) — correspond au binaire FREE/PREMIUM déjà utilisé partout.
const ENTITLEMENT_ID = 'premium';

// À renseigner une fois le projet RevenueCat créé (Phase 2 du plan de
// migration) : clé API publique Android, PAS la clé secrète.
const ANDROID_API_KEY = process.env.REACT_APP_REVENUECAT_ANDROID_KEY || '';

// Marque (par utilisateur) que le Premium actuel vient d'un achat Google Play.
// Clé séparée de 'user_subscription', que d'autres routines réécrivent ou purgent.
const PLAY_PREMIUM_KEY = 'premium_source_play';

let configured = false;
let currentUserId: string | null = null;
let currentUserEmail: string | null = null;

function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android';
}

/**
 * À appeler une fois au démarrage de l'app (même endroit que initAdMob()),
 * avec l'id Supabase de l'utilisateur connecté — c'est ce même id que le
 * backend utilise comme app_user_id RevenueCat pour relier les événements
 * du webhook à la bonne ligne `subscriptions.user_id`.
 */
export async function configure(userId: string, email?: string | null): Promise<void> {
  if (!isAndroidNative()) return;

  if (!ANDROID_API_KEY) {
    // Pas encore configuré côté RevenueCat (Phase 2 du plan) — on ne bloque
    // pas l'app, elle continue avec le tier déjà en cache localement.
    return;
  }

  if (configured) {
    // Déjà configuré pour ce compte : rien à faire.
    if (userId === currentUserId) return;

    // Autre compte connecté dans la même session : on bascule l'identité
    // RevenueCat, sinon un achat serait crédité au compte précédent.
    try {
      const { customerInfo } = await Purchases.logIn({ appUserID: userId });
      currentUserId = userId;
      currentUserEmail = email || null;
      applyCustomerInfo(customerInfo);
    } catch {
      // silencieux — même politique que la configuration initiale
    }
    return;
  }

  currentUserId = userId;
  currentUserEmail = email || null;

  try {
    await Purchases.configure({ apiKey: ANDROID_API_KEY, appUserID: userId });
    configured = true;

    await Purchases.addCustomerInfoUpdateListener((customerInfo: CustomerInfo) => {
      applyCustomerInfo(customerInfo);
    });

    // Resynchronise l'état local dès la config, au cas où l'entitlement
    // aurait changé pendant que l'app était fermée (renouvellement, expiration).
    const { customerInfo } = await Purchases.getCustomerInfo();
    applyCustomerInfo(customerInfo);
  } catch {
    // RevenueCat indisponible (hors ligne, mauvaise config...) — silencieux,
    // même politique que initAdMob().
  }
}

/**
 * Récupère les deux offres (mensuelle/annuelle) avec prix localisés depuis
 * le Play Store, pour remplacer les libellés "2€"/"20€" codés en dur dans
 * SubscriptionModal.tsx sur Android natif.
 */
export async function getOfferings(): Promise<{
  monthly: PurchasesPackage | null;
  yearly: PurchasesPackage | null;
}> {
  if (!isAndroidNative()) return { monthly: null, yearly: null };

  try {
    const offerings = await Purchases.getOfferings();
    const current: PurchasesOffering | null = offerings.current;
    return {
      monthly: current?.monthly ?? null,
      yearly: current?.annual ?? null,
    };
  } catch {
    return { monthly: null, yearly: null };
  }
}

/**
 * Déclenche l'achat natif Google Play. Retourne false (sans lever d'erreur)
 * si l'utilisateur a simplement fermé la fenêtre d'achat.
 */
export async function purchasePackage(pkg: PurchasesPackage): Promise<boolean> {
  try {
    const { customerInfo } = await Purchases.purchasePackage({ aPackage: pkg });
    applyCustomerInfo(customerInfo);
    return true;
  } catch (error: any) {
    if (error?.userCancelled) {
      return false;
    }
    throw error;
  }
}

/**
 * Requis par Google : affordance "Restaurer mes achats" en cas de
 * réinstallation ou de changement d'appareil.
 */
export async function restorePurchases(): Promise<boolean> {
  try {
    const { customerInfo } = await Purchases.restorePurchases();
    applyCustomerInfo(customerInfo);
    return !!customerInfo.entitlements.active[ENTITLEMENT_ID];
  } catch {
    return false;
  }
}

function getPlayPremiumUserId(): string | null {
  try { return localStorage.getItem(PLAY_PREMIUM_KEY); } catch { return null; }
}

function clearPlayPremiumMarker(): void {
  try { localStorage.removeItem(PLAY_PREMIUM_KEY); } catch {}
}

// Reproduit le triple pattern de paymentService.ts::updateSubscriptionAfterPayment
// (mise à jour du service + réécriture du cache localStorage + event), avec
// verifiedBy: 'revenuecat' au lieu de 'stripe_payment'.
function applyCustomerInfo(customerInfo: CustomerInfo): void {
  const entitlement = customerInfo.entitlements.active[ENTITLEMENT_ID];

  if (!entitlement) {
    // Pas d'entitlement actif. On ne repasse en FREE que si le Premium de CE
    // compte venait d'un achat Google Play (abonnement expiré, remboursé...).
    // Un abonné Stripe (web) qui utilise l'app Android n'a jamais
    // d'entitlement RevenueCat : il ne doit pas être rétrogradé ici.
    if (currentUserId && getPlayPremiumUserId() === currentUserId) {
      clearPlayPremiumMarker();
      subscriptionService.updateSubscription(SubscriptionTier.FREE, null, undefined, undefined, 'free_plan');
    }
    return;
  }

  if (currentUserId) {
    try { localStorage.setItem(PLAY_PREMIUM_KEY, currentUserId); } catch {}
  }

  // Même règle que le backend (upsertActivePlaySubscription) : l'ID du forfait
  // Play peut être en anglais ("yearly") ou en français ("annuel").
  const productIdLower = entitlement.productIdentifier.toLowerCase();
  const billingPeriod: 'monthly' | 'yearly' =
    productIdLower.includes('yearly') || productIdLower.includes('annuel')
      ? 'yearly'
      : 'monthly';
  const planId = billingPeriod === 'yearly' ? 'premium_yearly' : 'premium_monthly';
  const expiresAt = entitlement.expirationDateMillis;

  subscriptionService.updateSubscription(
    SubscriptionTier.PREMIUM,
    expiresAt,
    `revenuecat_${entitlement.productIdentifier}`,
    billingPeriod,
    planId,
    true // entitlement confirmé par RevenueCat / Google Play
  );

  const cacheData: Record<string, unknown> = {
    tier: 'premium',
    status: 'active',
    verified: true,
    verifiedBy: 'revenuecat',
    verifiedAt: Date.now(),
    cachedAt: Date.now(),
    plan_id: planId,
    billing_period: billingPeriod,
    expires_at: expiresAt,
  };
  if (currentUserEmail) {
    cacheData.email = currentUserEmail;
  }

  localStorage.setItem('user_subscription', JSON.stringify(cacheData));

  window.dispatchEvent(
    new CustomEvent('subscriptionUpdated', {
      detail: {
        tier: SubscriptionTier.PREMIUM,
        planId,
        billingPeriod,
        isPremium: true,
      },
    })
  );
}

const revenueCatService = {
  configure,
  getOfferings,
  purchasePackage,
  restorePurchases,
  isAndroidNative,
};

export default revenueCatService;
